import { useState } from "react";

import { type BoilerRoom, type Building, type HeatingNetwork } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { Circle } from "@eisox/design-system";
import { BarChartIcon, NutIcon } from "@eisox/icons";

import { HybridTooltip } from "~/components/hybrid-tooltip";
import { type RoomDataMap } from "~/features/room";
import { useBuildingContext } from "~/providers/building.provider";
import type { Permission } from "~/types/auth.type";
import type { EnhancedValve } from "~/types/valve.type";

import { ValvesDrawer } from "./valve-drawer";
import { ValveHistoryDialog } from "./valve-history-dialog";
import { ValveTooltipContent } from "./valve-tooltip-content";

interface ValveTooltipProps {
  children?: React.ReactElement;
  valve: EnhancedValve;
  roomData: RoomDataMap;
  building: Building;
  boilerRooms: BoilerRoom[];
  heatingNetworks: HeatingNetwork[];
  valves: EnhancedValve[];
  permissions: Permission;
}

const VALVE_TOOLTIP_NAME = "ValveTooltip";

export const ValveTooltip: React.FC<ValveTooltipProps> = ({
  valve,
  roomData,
  building,
  boilerRooms,
  heatingNetworks,
  valves,
  permissions,
  children,
}) => {
  const [valvesDrawerOpen, setValvesDrawerOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const { valves: buildingValves } = useBuildingContext(VALVE_TOOLTIP_NAME);

  const { id, uid, mac, problemStatus, updatedAt } = valve;

  return (
    <>
      <HybridTooltip
        content={
          <div className="gap-5 z-1300 flex flex-col">
            <div className="md:text-base gap-1.25 flex items-center text-sm text-primary-white [&_span]:text-primary-dark-gray">
              {uid} - {mac ?? "--"} <span>({updatedAt ? dayjs(updatedAt).format("L LTS") : "-"})</span>
              {permissions.valve?.read && (
                <Circle.Root
                  className="md:h-6.25! md:w-6.25! h-5! w-5!"
                  onClick={() => setValvesDrawerOpen(true)}
                  style={{ cursor: "pointer" }}
                  backgroundColor="gray"
                  color="darkBlue"
                >
                  <NutIcon />
                </Circle.Root>
              )}
              {mac && permissions.history?.valve.histoValve?.read && (
                <Circle.Root
                  className="md:h-6.25! md:w-6.25! h-5! w-5!"
                  onClick={() => setHistoryOpen(true)}
                  style={{ cursor: "pointer" }}
                  backgroundColor="gray"
                  color="darkBlue"
                >
                  <BarChartIcon />
                </Circle.Root>
              )}
              {problemStatus && (
                <Circle.Root
                  className="ml-auto"
                  backgroundColor={problemStatus === "error" ? "red" : "orange"}
                  size={10}
                />
              )}
            </div>
            <ValveTooltipContent valve={valve} roomData={roomData} permissions={permissions} variant="tooltip" />
          </div>
        }
      >
        {children}
      </HybridTooltip>

      {valvesDrawerOpen && (
        <ValvesDrawer
          open={valvesDrawerOpen}
          onOpenChange={setValvesDrawerOpen}
          building={building}
          boilerRooms={boilerRooms}
          heatingNetworks={heatingNetworks}
          valves={valves}
          permissions={permissions}
          defaultSelectedValveId={id}
        />
      )}

      {historyOpen && (
        <ValveHistoryDialog
          open
          onOpenChange={setHistoryOpen}
          buildingId={building.id}
          valves={buildingValves}
          valveId={id}
        />
      )}
    </>
  );
};

ValveTooltip.displayName = VALVE_TOOLTIP_NAME;
